import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, MessageCircle, Menu, X, ChevronDown, ArrowRight, LogIn } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { company, nav, whatsappUrl } from "../data/site";
import { services } from "../data/services";
import logo from "../assets/ruempeltaxi-logo.png";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-light-gray bg-white/95 backdrop-blur-xl"
          : "border-b border-transparent bg-white/80 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="container-page flex h-20 items-center justify-between gap-4">
        <Link to="/" className="flex shrink-0 items-center" data-testid="logo-link" aria-label="Rümpeltaxi Startseite">
          <img src={logo} alt="Rümpeltaxi" className="h-9 w-auto md:h-11" />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" aria-label="Hauptnavigation">
          {nav.map((item) =>
            item.to === "/leistungen" ? (
              <DropdownMenu key={item.to}>
                <DropdownMenuTrigger
                  className="group flex items-center gap-1 font-heading text-sm font-semibold text-anthracite outline-none transition-colors hover:text-yellow-dark"
                  data-testid="nav-leistungen-trigger"
                >
                  Leistungen
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="grid max-h-[70vh] w-72 grid-cols-1 gap-0.5 overflow-y-auto">
                  <DropdownMenuItem asChild>
                    <Link to="/leistungen" className="font-semibold" data-testid="nav-leistungen-all">
                      Alle Leistungen
                    </Link>
                  </DropdownMenuItem>
                  {services.map((s) => (
                    <DropdownMenuItem key={s.slug} asChild>
                      <Link to={`/leistungen/${s.slug}`} data-testid={`nav-service-${s.slug}`}>
                        {s.title}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `font-heading text-sm font-semibold transition-colors hover:text-yellow-dark ${
                    isActive ? "text-yellow-dark" : "text-anthracite"
                  }`
                }
                data-testid={`nav-link-${item.to}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={company.phoneLink}
            className="hidden items-center gap-2 font-heading text-sm font-bold text-anthracite transition-colors hover:text-yellow-dark lg:flex"
            data-testid="header-phone"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-light-gray">
              <Phone className="h-4 w-4" />
            </span>
            {company.phoneDisplay}
          </a>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden h-9 w-9 items-center justify-center rounded-sm bg-light-gray text-anthracite transition-colors hover:bg-yellow lg:flex"
            aria-label="WhatsApp"
            data-testid="header-whatsapp"
          >
            <MessageCircle className="h-4 w-4" />
          </a>
          <Link
            to="/kontakt"
            className="hidden items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black transition-colors hover:bg-yellow-dark md:inline-flex"
            data-testid="header-cta"
          >
            Unverbindlich anfragen
          </Link>
          <Link
            to="/login"
            className="hidden h-9 w-9 items-center justify-center rounded-sm bg-light-gray text-anthracite transition-colors hover:bg-yellow lg:flex"
            aria-label="Login – Verwaltung & Mitarbeiterbereich"
            title="Login"
            data-testid="header-login"
          >
            <LogIn className="h-4 w-4" />
          </Link>

          <button
            className="flex h-11 w-11 items-center justify-center rounded-sm bg-anthracite text-white xl:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-light-gray bg-white xl:hidden"
            data-testid="mobile-menu"
          >
            <nav className="container-page flex flex-col py-4">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `border-b border-light-gray py-3.5 font-heading text-base font-semibold ${
                      isActive ? "text-yellow-dark" : "text-anthracite"
                    }`
                  }
                  data-testid={`mobile-nav-${item.to}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/kontakt"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-sm bg-yellow px-4 py-3.5 font-heading font-bold text-brand-black"
                data-testid="mobile-cta"
              >
                Unverbindlich anfragen <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-sm border border-light-gray px-4 py-3 font-heading font-semibold text-anthracite"
                data-testid="mobile-login"
              >
                <LogIn className="h-4 w-4" /> Login / Mitarbeiterbereich
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
